import React, { FC, useEffect, useState } from "react";
import { TextField } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { Done } from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";
import dynamic from "next/dynamic";
import useSWR from "swr";
import { ControllerRenderProps } from "react-hook-form";
import { UseFormStateReturn } from "react-hook-form/dist/types";

interface Option {
  value: string;
  name: string;
  type: "Future" | "Past";
}

const useStyles = makeStyles(() => ({
  listbox: {
    '& > li[aria-selected="true"]': {
      backgroundColor: "#EDFBFE",
    },
  },
  selectTitle: {
    marginLeft: 12,
    color: "#535759",
    fontWeight: 500,
    fontSize: 14,
    marginRight: 8,
  },
}));

interface FiscalQuarter {
  endDate: string;
  name: string;
  shortName: string;
  startDate: string;
  children: FiscalQuarter[];
}

const QVRDialogFiscalQuarter: FC<ControllerRenderProps & UseFormStateReturn<any>> = (props) => {
  const classes = useStyles();
  const { data: quarter } = useSWR<{
    nowAndFuture: FiscalQuarter[];
    past: FiscalQuarter[];
  }>(`/api/dataservice/v1/metadata/renewal-quarters?onlyQuarter=true`);
  const [options, setOptions] = useState<Option[]>([]);

  const [value, setValue] = useState<Option | null>(null);
  useEffect(() => {
    if (quarter) {
      const options: Option[] = [];
      quarter.past.reduce((prv, cur) => {
        prv.push(
          ...cur.children.map(
            (x) =>
              ({
                name: x.name,
                value: x.name.replace(" ", ""),
                type: "Past",
              }) as Option
          )
        );
        return prv;
      }, options);
      quarter.nowAndFuture.reduce((prv, cur) => {
        prv.push(
          ...cur.children.map(
            (x) =>
              ({
                name: x.name,
                value: x.name.replace(" ", ""),
                type: "Future",
              }) as Option
          )
        );
        return prv;
      }, options);
      setOptions(options);
    }
  }, [quarter]);

  useEffect(() => {
    if (props.value && options.length) {
      const option = options.find((x) => x.value === props.value) ?? null;
      setValue(option);
    }
  }, [props.value, options.length]);

  const onChange = (_e: any, value: Option) => {
    setValue(value);
    props.onChange(value.value);
  };

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Autocomplete
        classes={{ listbox: classes.listbox }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            inputProps={{
              ...params.inputProps,
            }}
            InputProps={{
              ...params.InputProps,
            }}
            size={"small"}
            error={!!props.errors?.[props.name]}
            helperText={props.errors?.[props.name]?.message}
          />
        )}
        options={options}
        loading={options.length === 0}
        loadingText={"Loading..."}
        // groupBy={(o) => o.type}
        fullWidth={true}
        isOptionEqualToValue={(o, v) => o.value === v.value}
        getOptionLabel={(o) => o.name}
        disableClearable={true}
        renderOption={(props, option, { inputValue, selected }) => {
          const regex = new RegExp(inputValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "ig");
          const spanText = option.name.replace(
            regex,
            `<span style="background-color:#FFE399; font-size: 14px">$&</span>`
          );
          return (
            <li {...props}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: 14,
                      color: "#333",
                    }}
                    dangerouslySetInnerHTML={{ __html: spanText }}
                  />
                </div>
                {selected ? <Done style={{ color: "#007AA3" }} /> : null}
              </div>
            </li>
          );
        }}
        size={"small"}
        onChange={onChange}
        //@ts-ignore
        value={value}
      />
    </div>
  );
};

export default dynamic(() => Promise.resolve(QVRDialogFiscalQuarter), {
  ssr: false,
});
